import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDisplayDate } from '@/utils/date-formatters';

import {
  TimeOffDayValidationError,
  TimeOffDayValidationType,
} from '../../helpers/day-validation-errors.helper';

interface TimeOffDayOverlapModalProps {
  open: boolean;
  onClose: () => void;
  /** The per-day validation errors returned by the backend (code 2808). */
  errors: TimeOffDayValidationError[];
}

/**
 * Shown when a create request fails day-level validation (backend code 2808):
 * lists each offending day and why it failed (e.g. it overlaps an existing
 * time-off). The user dismisses it, adjusts the request, and retries.
 */
export const TimeOffDayOverlapModal = ({ open, onClose, errors }: TimeOffDayOverlapModalProps) => {
  const { t } = useTranslation('authenticated.timeOff');

  const resolveReason = (type: TimeOffDayValidationError['type']): string => {
    if (type === TimeOffDayValidationType.Overlap) {
      return t('details.dayValidation.reasons.overlap');
    }
    return t('details.dayValidation.reasons.generic');
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="h-5 w-5 text-destructive" />
            {t('details.dayValidation.title')}
          </DialogTitle>
          <DialogDescription>{t('details.dayValidation.description')}</DialogDescription>
        </DialogHeader>

        <div className="max-h-72 overflow-y-auto overflow-x-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t('details.dayValidation.columns.date')}</TableHead>
                <TableHead>{t('details.dayValidation.columns.reason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {errors.map(error => (
                <TableRow key={error.date} className="hover:bg-transparent">
                  <TableCell className="font-medium">{formatDisplayDate(error.date)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {resolveReason(error.type)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>{t('details.dayValidation.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
